import React, { useRef, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Tooltip, LabelList } from "recharts";
import styles from "../../../../components/barAndLineComboChart/barAndLineComboChart.module.css";

const BAR_WIDTH = 80;
const WINDOW_SIZE = 10;

/* ---------- FORMATTERS ---------- */
const formatNumber = (value) =>
  value !== undefined && value !== null
    ? value.toLocaleString()
    : "0";

/* ---------- SAFE VALUE FOR LOG SCALE ---------- */
/* log scale cannot accept 0 → internally map 0 to EPSILON */
const EPSILON = 0.0001;

const safeValue = (v) => (v > 0 ? v : EPSILON);


const formatToCappedUnit = (value) => {
  if (!value || value === null || value === undefined) return null; // hide fake log zero


  if (value < 1000) {
    return `${value}`;
  }

  if (value < 100000) {
    // Thousands (T)
    return `${(value / 1000).toFixed(1)} K`;
  }

  if (value < 10000000) {
    // Lakhs
    return `${(value / 100000).toFixed(1)} L`;
  }

  // Crores
  return `${(value / 10000000).toFixed(1)} Cr`;
};
const renderTopBarLabel = ({ x, y, width, value }) => {

  if (value === EPSILON) {
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        fill="#1f2933"
        fontSize={10}
        fontWeight={700}
      >
        0
      </text>
    );
  }

  if (value === null || value === undefined) return null;

  return (
    <text
      x={x + width / 2}
      y={y - 6}                 //  outside top of bar
      textAnchor="middle"
      fill="#1f2933"
      fontSize={10}
      fontWeight={700}
    >
      {formatToCappedUnit(value)}
    </text>
  );
};

const CustomizedAxisTick = ({ x, y, payload }) => (
  <g transform={`translate(${x},${y})`}>
    <text textAnchor="middle" fontSize={9} fill="#2B2B2B">
      {payload?.value}
    </text>
  </g>
);

const CompactTooltip = ({ active, payload, label, coordinate }) => {
  const payloadItems = Array.isArray(payload) ? payload : [];

  if (!active || payloadItems[0] === undefined || !coordinate) {
    return null;
  }

  const tooltipWidth = 180;
  const tooltipHeight = 70;

  const container = document.querySelector(`.${styles.scrollbarContainer}`);
  const rect = container?.getBoundingClientRect();
  if (!rect) return null;

  let left = rect.left + coordinate.x + 10;
  let top = rect.top + coordinate.y - tooltipHeight + 15;

  if (left + tooltipWidth > rect.right - 10) {
    left = rect.right - tooltipWidth - 10;
  }

  if (left < rect.left + 10) {
    left = rect.left + 10;
  }

  if (top < rect.top + 10) {
    top = rect.top + coordinate.y + 15;
  }

  if (top + tooltipHeight > rect.bottom) {
    top = rect.bottom - tooltipHeight - 10;
  }

  return (
    <div
      style={{
        position: "fixed",
        left,
        top,
        background: "#fff",
        border: "1px solid #d9d9d9",
        borderRadius: 4,
        padding: "6px 8px",
        fontSize: 10,
        boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
        zIndex: 9999,
        pointerEvents: "none",
        width: tooltipWidth,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>

      {payloadItems.map((entry) => (
        <div key={entry.dataKey} style={{ color: entry.color }}>
          {entry.name}:{" "}
          {entry.value === EPSILON ? "0" : entry.value?.toLocaleString?.()}
        </div>
      ))}
    </div>
  );
};

const MaterialViewGraph = ({ data = [] }) => {
  const [startIndex, setStartIndex] = useState(0);
  const chartContainerRef = useRef(null);

  // Debug logging for incoming data
  useEffect(() => {
    if (data && data.length > 0) {
      // data received successfully
    } else {
      console.warn('[MaterialViewGraph] No data received from API');
    }
  }, [data]);

  /* ---------- WINDOWED DATA ---------- */
  const visibleData = useMemo(
    () => data.slice(startIndex, startIndex + WINDOW_SIZE),
    [data, startIndex],
  );

  /* ---------- HORIZONTAL SCROLL ---------- */
  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    let animationFrameId;
    const handleScroll = () => {
      animationFrameId = requestAnimationFrame(() => {
        const newStart = Math.floor(container.scrollLeft / BAR_WIDTH);
        if (
          newStart !== startIndex &&
          newStart >= 0 &&
          newStart <= data.length - WINDOW_SIZE
        ) {
          setStartIndex(newStart);
        }
      });
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [data.length, startIndex]);

  /* ---------- TRANSFORM DATA ---------- */
  const transformedData = useMemo(
    () => {
      const transformed = visibleData.map((item) => ({
        ResidualLoss: safeValue(item[`Residual Loss`] || 0),
        ShipmentQty: safeValue(item[`Shipment Qty`] || 0),
        Days: item["Days"] || "",
      }));
      
      return transformed;
    },
    [visibleData],
  );

  /* ---------- RIGHT AXIS DOMAIN ---------- */
  const shipmentMax = Math.max(
    ...transformedData.map((d) => d.ShipmentQty).filter(v => v !== EPSILON),
    0,
  );

  const rightDomain = [0, shipmentMax > 0 ? shipmentMax * 1.2 : 1];
  const isScrollable = data.length > WINDOW_SIZE;

  // Show message when no data
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: 280,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#999",
          fontSize: 14,
          fontWeight: 500,
          background: "#f9f9f9",
          borderRadius: 4,
          border: "1px solid #e0e0e0",
        }}
      >
        No data available for the selected filters
      </div>
    );
  }

  return (
    <div
      className={styles.scrollbarContainer}
      ref={chartContainerRef}
      style={{
        overflowX: isScrollable ? "auto" : "hidden",
        width: "100%",
        position: "relative",
        marginTop: "5px",
      }}
    >
      {/* phantom width for scroll */}
      <div
        style={{
          width: isScrollable ? `${data.length * BAR_WIDTH}px` : "100%",
          height: 280,
          position: "relative",
          overflow: "visible",
        }}
      >
        {/* windowed chart */}
        <div
          style={{
            position: "absolute",
            left: `${startIndex * BAR_WIDTH}px`,
            width: isScrollable ? `${WINDOW_SIZE * BAR_WIDTH}px` : "100%",
            height: 280,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={transformedData}
              barGap={4}
              barCategoryGap={20}
              margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />

              {/* X AXIS */}
              <XAxis
                dataKey="Days"
                tick={<CustomizedAxisTick />}
                interval={0}
                height={50}
                axisLine={false}
                tickLine={false}
                tickMargin={10}
              />

              {/* LEFT Y AXIS – ORDER LOSS (LOG SCALE) */}
              <YAxis
                yAxisId="left"
                scale="log"
                domain={["dataMin", "dataMax"]}
                tickFormatter={formatNumber}
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={90}
              />

              {/* RIGHT Y AXIS – SHIPMENTS (LINEAR) */}
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={rightDomain}
                allowDecimals={false}
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={55}
              />
              {/* TOOLTIP (VALUES ON HOVER ONLY) */}
              <Tooltip
                content={<CompactTooltip />}
                isAnimationActive={false}
                allowEscapeViewBox={{ x: true, y: true }}
                offset={5}
                cursor={{ fill: "rgba(0,0,0,0.05)" }}
              />
              <Legend
                verticalAlign="bottom"
                align="left"
                wrapperStyle={{ fontSize: "10px", color: "black" }}
              />

              {/* BARS */}
              <Bar
                yAxisId="left"
                dataKey="ResidualLoss"
                name={`Residual Loss`}
                fill="rgb(0, 94, 239)"
                barSize={BAR_WIDTH}
                minPointSize={2}
              >
                <LabelList content={renderTopBarLabel} />
              </Bar>
              <Bar
                yAxisId="left"
                dataKey="ShipmentQty"
                name={`Shipment Qty`}
                fill="rgb(0, 177, 144)"
                barSize={BAR_WIDTH}
                minPointSize={2}
              >
                <LabelList content={renderTopBarLabel} />
              </Bar>
              {/* LINE */}
              {/* <Line
                yAxisId="right"
                type="bump"
                dataKey="MaxOfAging"
                name="Max of Aging"
                stroke="#e7381d"
                strokeWidth={1.3}
                strokeOpacity={0.9}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              /> */}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

MaterialViewGraph.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
};

CustomizedAxisTick.propTypes = {
  x: PropTypes.number,
  y: PropTypes.number,
  payload: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

CompactTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string,
      color: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  coordinate: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
};

export default React.memo(MaterialViewGraph);